import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Flask } from "@phosphor-icons/react";
import Captcha from "../../components/public/Captcha";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState({ token: "", answer: "" });
  const [captchaKey, setCaptchaKey] = useState(0);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!captcha.token || !captcha.answer) { setErr("Please solve the CAPTCHA"); return; }
    setBusy(true);
    const res = await login(email, password, captcha);
    setBusy(false);
    if (res.ok) {
      const dest = location.state?.from?.pathname || "/admin";
      navigate(dest, { replace: true });
    } else {
      setErr(res.error);
      // Force a fresh CAPTCHA — the old one is single-use, consumed on the server.
      setCaptchaKey((k) => k + 1);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F6] grid lg:grid-cols-2" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Left visual panel */}
      <div className="hidden lg:flex bg-[#C2410C] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 30% 20%, white 0.5px, transparent 1px), radial-gradient(circle at 70% 80%, white 0.5px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <Flask size={28} weight="duotone" className="text-[#0EA5E9]" />
            <span className="text-xs tracking-[0.3em] uppercase text-white/70">Bonheur Pharma</span>
          </div>
          <div className="mt-12 max-w-md">
            <h2 className="text-4xl lg:text-5xl tracking-tight font-bold leading-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Operate every shelf of the catalogue from one console.
            </h2>
            <p className="mt-6 text-white/70 text-base leading-relaxed">
              Products, blogs, careers, inquiries — your team&apos;s pharmaceutical storefront, organised and instrumented.
            </p>
          </div>
        </div>
        <div className="relative text-xs text-white/50 tracking-wider">
          Established workflow · India
        </div>
      </div>

      {/* Right login */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm" data-testid="login-card">
          <div className="mb-8">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#4B5563]">Admin access</div>
            <h1 className="mt-1 text-3xl tracking-tight font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Sign in
            </h1>
            <p className="text-sm text-[#4B5563] mt-2">Enter your credentials to manage the website.</p>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4B5563]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@bonheurpharma.in"
                required
                data-testid="login-email"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#C2410C]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4B5563]">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#C2410C]"
              />
            </div>
            <Captcha
              onChange={setCaptcha}
              refreshKey={captchaKey}
              testId="login-captcha"
            />
            {err && (
              <div className="text-sm text-[#EF4444] bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full h-11 bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold tracking-wide transition-colors duration-200"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
